import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import Badge from 'components/crm/ui/Badge';
import { shortDay, useCrmLocale } from 'components/crm/ui/crmTime';
import useText from 'hooks/useText';
import { getProspectSummary } from 'server/crm/prospectionApi';

const FALLBACK_NOTES = 3;

const notedCalls = (history) =>
  (history || []).filter((call) => call.note && call.note.trim());

// « Résumé des derniers échanges »: the AI points of the prospect's call notes.
// history: the calls of the company, the latest first (null while loading).
// A new history (read again after a confirmed call) asks for the summary
// again; the points already shown stay until the new ones come.
const ExchangeSummary = ({ companyId, history }) => {
  const { text } = useText('prospection');
  const locale = useCrmLocale();
  const [points, setPoints] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const notes = notedCalls(history);
  const hasNote = notes.length > 0;

  useEffect(() => {
    if (!companyId || !hasNote) return undefined;
    // An answer for another company or an older history is ignored.
    let active = true;
    setLoading(true);
    getProspectSummary({ companyId }).then(
      (answer) => {
        if (!active) return;
        const answered = answer?.points?.length ? answer.points : null;
        setPoints(answered);
        setFailed(!answered);
        setLoading(false);
      },
      () => {
        if (!active) return;
        setPoints(null);
        setFailed(true);
        setLoading(false);
      },
    );
    return () => {
      active = false;
    };
    // history: a reload after a confirmed call gives a new array.
  }, [companyId, hasNote, history]);

  if (!hasNote) return null;

  let body;
  if (points) {
    body = (
      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm leading-5 text-body">
        {points.map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
    );
  } else if (loading || !failed) {
    body = (
      <div
        role="status"
        aria-label={text('ai.summaryLoading')}
        className="flex flex-col gap-2"
      >
        <span
          data-testid="summary-loading-bar"
          className="h-3 w-full animate-pulse rounded bg-surfaceMuted"
        />
        <span
          data-testid="summary-loading-bar"
          className="h-3 w-2/3 animate-pulse rounded bg-surfaceMuted"
        />
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-muted">{text('ai.summaryFallback')}</p>
        <ul className="flex flex-col gap-1 text-sm leading-5 text-body">
          {notes.slice(0, FALLBACK_NOTES).map((call, index) => (
            <li key={call.id ?? index}>
              {call.at ? (
                <span className="text-muted">
                  {shortDay(new Date(call.at), locale)} ·{' '}
                </span>
              ) : null}
              {call.note.trim()}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line p-4">
      <div className="flex items-center gap-2">
        <p className="font-title text-sm font-medium leading-5 text-body">
          {text('ai.summaryTitle')}
        </p>
        {points ? (
          <Badge tone="bg-primary-tint text-primary-dark">
            <Sparkles size={12} aria-hidden="true" />
            {text('ai.badge')}
          </Badge>
        ) : null}
      </div>
      {body}
    </div>
  );
};

export default ExchangeSummary;
